/**
 * StickyCartBar — fixed-bottom CTA shown on every customer shop surface
 * when the cart for that salon has at least one item. Tapping opens the
 * CartModal; tapping again on Checkout pushes /:slug/checkout.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiCheckCircle, FiTrash2, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import basketIcon from '../../assets/basket.svg';
import { useCart, useCartAbandonmentHeartbeat } from '../../Context/CartContext';
import { useCurrency } from '../../Context/CurrencyContext';
import { currencySymbol, shopAmount, shopPrice } from '../../utils/currency';
import { renditionUrl } from '../../utils/imageRenditions';
import { getRelatedProducts, trackStoreEvent } from '../../api/storeApi';
import { currentShop } from '../../utils/currentShop';
import { effectiveVariantPrice } from '../../utils/productPrice';
import { effectiveVariantPrice as shownVariantPrice } from '../../utils/shopPromotions';
import { hasCap, lineCap, productPurchaseCap, variantPurchaseCap } from '../../utils/stockCap';
import { getBasePath } from '../../utils/domainMode';
import { shopSlugOfPath } from '../../utils/shopSlugOfPath';
import RecMiniCard from '../RecMiniCard';
import PrivacyNotice from '../PrivacyNotice';
import { useSwipeToReveal } from '../../hooks/useSwipeToReveal';
import useCartStockCaps from '../../hooks/useCartStockCaps';
import useCartPricing from '../../hooks/useCartPricing';
import { campaignNamesLabel } from '../../utils/campaignNamesLabel';
import { useDraggableSheet } from '../../hooks/useDraggableSheet';
import usePublishedHeight from '../../hooks/usePublishedHeight';
import { isolate, localizedProduct, variantOptionsIn } from '../../utils/shopperText';
import { hidesItsPrice, isAskProduct } from '../../utils/orderMode';
import styles from './styles.module.css';

const CLOSE_MS = 280;
/* The room `.bar` floats above the screen's edge (`bottom: calc(16px + env(safe-area-inset-bottom))`),
 * published with its height so a button fixed above it clears the bar and its gap. */
const BAR_GAP_PX = 16;
/* THE QUIET SCROLLBAR'S TRAILING WINDOW.
 *
 * `.modalScroll` composes `quietScroll` (declared once in
 * `Pages/Shop/styles.module.css`), whose thumb is transparent at rest and
 * painted while the box is being scrolled — macOS's own overlay behaviour.
 * The stylesheet handles hover and focus-within on its own; the "is moving"
 * half has to come from the component, and this is how long it stays true
 * after the last scroll event.
 *
 * It is the side panel's own number. `ShopMenuDrawer` exports `QUIET_MS` and
 * `cartBottomEdgeAndScrollbar.test.js` asserts these two are equal, so the
 * value cannot drift between the two surfaces — but it is written out rather
 * than imported, because importing it would pull the whole side-panel module
 * into every storefront page for the sake of one integer. */
const SCROLL_QUIET_MS = 700;
const MAX_RECENTLY_REMOVED = 6;
/* How long the "added" toast stays (the owner, 2026-10-26: after the first item the drawer stays out of the
 * way). Long enough to read two words and reach "Checkout", short enough not to sit on the page. */
const TOAST_MS = 4000;
/* THE FIRST-ITEM MARKER — sessionStorage, this visit only: the drawer has already opened by itself for an add
 * to this shop's basket. A basket restored from an earlier visit has no marker but has lines, and that is
 * not a first item either (see the handler below). */
const autoOpenedKey = (slug) => `stylon:cart:auto-opened:${slug}`;
const readMarker = (slug) => {
  try { return !!window.sessionStorage.getItem(autoOpenedKey(slug)); } catch { return false; }
};
const writeMarker = (slug) => {
  try { window.sessionStorage.setItem(autoOpenedKey(slug), '1'); } catch { /* private mode: every add then looks like a first one, and opens — the old behaviour */ }
};
const clearMarker = (slug) => {
  try { window.sessionStorage.removeItem(autoOpenedKey(slug)); } catch { /* nothing to forget */ }
};

const StickyCartBar = () => {
  const { t } = useTranslation();
  const currency = useCurrency();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [pulse, setPulse] = useState(false);
  const slugFromUrl = shopSlugOfPath(location.pathname);
  const slug = params.salonSlug || params.username || params.slug || slugFromUrl;
  // Routes that ALWAYS suppress the floating cart bar (their own primary
  // action lives at the bottom of the page already, and the cart bar would
  // distract from / overlap that action). Applies to all viewport widths.
  const isCheckoutFlow = /\/(checkout|order-placed)$/.test(location.pathname);
  // The appointment booking flow owns the bottom of the screen with its own
  // summary bar (service, price, Next). The floating cart sits at z-index 50
  // and that bar at 1001, so the cart rendered BEHIND it — its rounded ends
  // poking out either side of the square summary bar. Suppress it here for the
  // same reason as checkout: the page already has one primary action at the
  // bottom, and a second one competing for the same strip helps nobody.
  //
  // Matched at any depth: the platform serves /:salonSlug/add-appointment,
  // while a custom domain serves /add-appointment — the reserved-word list
  // above only ever caught the second of those.
  const isBookingFlow = /(^|\/)add-appointment(\/|$)/.test(location.pathname);
  // The product page's own sticky CTA only stacks with the cart bar on mobile
  // — on desktop that CTA lives inline in the product info column, so the
  // cart bar can (and should) show so users see what they've added.
  const isProductPage = /\/p\/[^/]+$/.test(location.pathname);
  // The public course page owns the bottom of a phone screen with its own fixed
  // "Reserve my spot" bar (z-index 40), and this one floats at z-index 50 —
  // so the cart pill sat directly on top of the course's CTA. Same rule as the
  // product page above, for the same reason and at the same breakpoint: on a PC
  // that CTA lives inline in a sticky rail, so the cart bar can show.
  const isCoursePage = /(^|\/)courses\/[^/]+$/.test(location.pathname);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia?.('(min-width: 900px)').matches === true,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 900px)');
    const handler = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);
  const { items, count, updateQty, removeItem, applyStockCaps, applyListPrices } = useCart(slug || '__none__');
  const [open, setOpen] = useState(false);
  /* The line an add just put in, when the drawer opened because of that add —
   * so it can say so. Null when the shopper opened the cart themselves. */
  const [addedVariantId, setAddedVariantId] = useState(null);
  const [barMounted, setBarMounted] = useState(false);
  const barRef = useRef(null);
  const [barClosing, setBarClosing] = useState(false);
  const [barEntering, setBarEntering] = useState(false);
  const [countBump, setCountBump] = useState(false);
  const prevCountRef = useRef(count);

  const shouldShowBar = !!(
    slug
    && items.length > 0
    && !isCheckoutFlow
    && !isBookingFlow
    && (!isProductPage || isDesktop)
    && (!isCoursePage || isDesktop)
  );

  useEffect(() => {
    if (shouldShowBar) {
      setBarClosing(false);
      if (!barMounted) {
        // Only play the entrance animation once when the bar first mounts.
        // Re-using the `.bar` class for entrance caused a replay every time
        // `.barPulse` was added/removed (the `animation` property changed).
        setBarMounted(true);
        setBarEntering(true);
      }
      return undefined;
    }
    if (!barMounted) return undefined;
    setBarClosing(true);
    const timer = setTimeout(() => {
      setBarMounted(false);
      setBarClosing(false);
      setBarEntering(false);
    }, 320);
    return () => clearTimeout(timer);
  }, [shouldShowBar, barMounted]);

  /* THE BAR SAYS HOW TALL IT IS (DK parity, S1d). While it is on screen, `--sticky-cart-offset` on
   * the root is its height plus its gap; the moment it steps aside (the checkout, an emptied cart) or
   * the app lets it go, the number is withdrawn. The shop's floating help button reads it to stay
   * above the bar, and Stylon's footer bar reads it to leave room at the end of the page. */
  usePublishedHeight(barRef, { name: '--sticky-cart-offset', key: 'cart-bar', gap: BAR_GAP_PX, active: barMounted && shouldShowBar });

  useEffect(() => {
    if (!barEntering) return undefined;
    const t = setTimeout(() => setBarEntering(false), 400);
    return () => clearTimeout(t);
  }, [barEntering]);

  useEffect(() => {
    let timer;
    const handler = () => {
      setPulse(true);
      timer = setTimeout(() => setPulse(false), 650);
    };
    window.addEventListener('stylon:cart-pulse', handler);
    return () => {
      window.removeEventListener('stylon:cart-pulse', handler);
      if (timer) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (count === prevCountRef.current) return undefined;
    setCountBump(true);
    prevCountRef.current = count;
    const timer = setTimeout(() => setCountBump(false), 320);
    return () => clearTimeout(timer);
  }, [count]);

  /* ── THE SHELF, RE-READ ONCE WHILE THE DRAWER IS OPEN ────────────────────
   *
   * `snapshot.maxQty` already holds the `+` button at what the shop said when
   * the line was added, with no network at all. That memory can be old — 244
   * of dk-store's rows are down to their last unit — so opening the drawer
   * takes ONE batched reading of the products in the cart and clamps any line
   * that has since gone over. Gated on `open`: a shopper who never opens their
   * cart costs the server nothing. */
  const { products: shelfProducts } = useCartStockCaps(slug, items, {
    active: open,
    onCaps: (caps) => applyStockCaps?.(caps),
    // The same read carries each line's live LIST price; a price the owner
    // changed since the line went in is corrected before anyone checks out.
    onPrices: (prices) => applyListPrices?.(prices),
  });

  /* WHAT THE BASKET WILL BE CHARGED — the bar's number and the drawer's.
   *
   * The server's quote for the basket (the checkout's own arithmetic), with the
   * campaign display rule pricing each line and standing in for the total
   * while the quote is on its way. The bar is on screen far more often than
   * the drawer is open, so it asks whenever either is showing — and only then. */
  const pricing = useCartPricing(slug, items, {
    active: !!slug && items.length > 0 && (shouldShowBar || open),
    products: shelfProducts,
  });
  const total = pricing.total;

  /* WHEN THE DRAWER OPENS BY ITSELF (the owner, 2026-10-26: "directly open the cart for the first item added
   * (after that no need)").
   *
   * A door that has just ADDED something sends `detail.addedVariantId`. The FIRST item of a basket opens the drawer
   * as the confirmation of that add — "added to your cart", the line, the subtotal, checkout (DK Store, 2026-09-16:
   * an add on a phone showed nothing but a small bag). Every later add shows the small toast instead: still seen,
   * still one tap from checkout (the 09-16 guarantee), without a sheet on top of the page each time.
   *
   * "First" is judged two ways, so a restored basket is not mistaken for one: no marker for this visit
   * (`stylon:cart:auto-opened:<slug>`, written when the drawer opens because of an add, removed when the basket
   * empties) AND no line in the basket other than the one just added. Reading `other lines` rather than a count
   * keeps the answer the same whether the add has reached this component's state yet or not.
   *
   * A tap on the bag or the bar sends no `addedVariantId` and always opens. An add that arrives while the drawer is
   * already open (its own suggestions row does not send one; a door elsewhere might) leaves it open and says nothing
   * twice: the drawer shows the line. */
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const openRef = useRef(open);
  openRef.current = open;
  const slugRef = useRef(slug);
  slugRef.current = slug;
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  closingRef.current = closing;
  const [toast, setToast] = useState(null);

  /* ONE EVENT PER CONFIRMATION (not a tap: the shopper did nothing, the page showed them their add), so the
   * shop can tell a drawer from a toast by what the shopper did next. Through the store's batched analytics;
   * never throws, never waits. */
  const confirmed = (kind, variantId) => {
    try {
      const shop = currentShop();
      trackStoreEvent?.({ salonId: shop?.id, eventType: 'cart_confirm', payload: { kind, variantId } });
    } catch { /* analytics never blocks the shopper */ }
  };

  /* THE ONE CHECKOUT DOOR — the drawer's button and the toast's. A line the server said the shop no longer
   * sells does not go to checkout (the drawer said so above its button); it leaves the cart here, rather than
   * being refused at the last tap. */
  const goToCheckout = () => {
    (pricing.unavailable ?? []).forEach((variantId) => removeItem(variantId));
    navigate(`${getBasePath(slug)}/checkout`);
  };

  useEffect(() => {
    const handler = (e) => {
      const variantId = e?.detail?.addedVariantId ?? null;
      const shop = slugRef.current;
      /* 2026-10-27: a door that adds AND navigates in the same tap says so (`movesShopper`). Until today the
       * controller ruling below was served by an accident: on `/<slug>` the bar could not read the shop (the
       * empty-drawer bug), so `!shop` made the drawer open. Now that `/<slug>` names its shop, the ruling is
       * kept on purpose, by the door's own word, not by the bar being blind. */
      const movesShopper = !!e?.detail?.movesShopper;
      if (!variantId) {
        setAddedVariantId(null);
        setOpen(true);
        return;
      }
      if (openRef.current && !closingRef.current) {
        setAddedVariantId(variantId);
        confirmed('drawer', variantId);
        return;
      }
      // A drawer already on its way out (its ~280 ms close) counts as closed: the add is confirmed by the
      // toast, which shows as soon as the sheet has gone, instead of by nothing.
      const closing = openRef.current && closingRef.current;
      const hasOtherLines = itemsRef.current.some((i) => i.variantId !== variantId);
      /* AN ADD THAT MOVES THE SHOPPER OPENS THE DRAWER (`movesShopper`) — controller ruling, 2026-10-26. A door
       * that adds AND navigates in the same tap (the business home's product strip: navigate + open-cart) is
       * heard here before the page it lands on is in the address, and a toast would be cleared by the route
       * change (the pathname effect below) in the same render, leaving the add unconfirmed. So that door says
       * `movesShopper: true` and the drawer opens THERE, whatever the basket holds. This used to work only
       * because `/<slug>` could not be read (`!shop`, the empty-drawer bug); it is now the door's own word.
       * `!shop` stays as the fallback for any address that names no shop.
       *
       * ACCEPTED EDGE: a restored basket holding ONLY variant X, then an add of X again, looks the same as a
       * first add (no marker, no other line), so the drawer opens once more. An EMPTY basket is always a
       * first item, marker or not: a first add the shelf refused leaves a marker and no line, and the
       * real first item after it must still open the drawer. */
      if (!closing && (movesShopper || !shop || (!hasOtherLines && (itemsRef.current.length === 0 || !readMarker(shop))))) {
        if (shop) writeMarker(shop);
        setAddedVariantId(variantId);
        setOpen(true);
        confirmed('drawer', variantId);
        return;
      }
      setToast((prev) => ({ id: (prev?.id ?? 0) + 1 }));
      confirmed('toast', variantId);
    };
    window.addEventListener('stylon:open-cart', handler);
    return () => window.removeEventListener('stylon:open-cart', handler);
  }, []);

  // An emptied basket is a new basket: its next add is a first item again.
  useEffect(() => {
    if (slug && items.length === 0) clearMarker(slug);
  }, [slug, items.length]);

  // Navigating away takes the toast with it: it confirmed an add on the page the shopper has left.
  const pathnameForToast = location.pathname;
  useEffect(() => { setToast(null); }, [pathnameForToast]);

  // The toast goes by itself; a newer add restarts its time, and opening the drawer takes it away.
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), TOAST_MS);
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    if (open && !closing) setToast(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render when the bar should show OR the drawer is open (so the drawer can
  // appear on surfaces where the bottom bar is hidden, like the product page).
  if (!barMounted && !open && !toast) return null;

  return (
    <>
      {barMounted && (
      <button
        ref={barRef}
        type="button"
        className={`${styles.bar} ${barEntering ? styles.barEntering : ''} ${pulse ? styles.barPulse : ''} ${barClosing ? styles.barClosing : ''}`}
        onClick={() => setOpen(true)}
        data-testid="cart-sticky-bar"
        aria-label={t('cart.viewOrder', { defaultValue: 'View order' })}
      >
        <span className={styles.barIconWrap}>
          <span
            className={styles.barBasket}
            style={{ maskImage: `url(${basketIcon})`, WebkitMaskImage: `url(${basketIcon})` }}
            aria-hidden
          />
          <span className={`${styles.barCount} ${countBump ? styles.barCountBump : ''}`}>
            {count}
          </span>
        </span>
        <span className={styles.barLabel}>
          <span>
            {t('cart.productsCount', { defaultValue: '{{n}} products', count, n: count })}
          </span>
          <span className={styles.barLabelSep} aria-hidden> · </span>
          <AnimatedMoney amount={total} currency={currency} className={styles.barTotal} suffix={currencySymbol(currency)} />
        </span>
        <span className={styles.barCta}>{t('cart.view', { defaultValue: 'View' })}</span>
      </button>
      )}

      {toast && (!open || closing) && (
        <div className={`${styles.addedToast} ${barMounted ? styles.addedToastAboveBar : ''}`} role="status" data-testid="cart-added-toast">
          <FiCheckCircle className={styles.addedToastIcon} aria-hidden />
          <span className={styles.addedToastText}>
            {t('cart.addedToCart', { defaultValue: 'Added to your cart' })}
          </span>
          <button
            type="button"
            className={styles.addedToastBtn}
            onClick={() => { setToast(null); goToCheckout(); }}
            data-testid="cart-added-toast-checkout"
          >
            {t('cart.checkoutShort', { defaultValue: 'Checkout' })}
          </button>
        </div>
      )}

      {open && (
        <CartModal
          slug={slug}
          items={items}
          total={total}
          pricing={pricing}
          products={shelfProducts}
          addedVariantId={addedVariantId}
          onClose={() => { setOpen(false); setClosing(false); setAddedVariantId(null); }}
          onCheckout={goToCheckout}
          onClosing={() => setClosing(true)}
          closeDurationMs={CLOSE_MS}
          onUpdateQty={updateQty}
          onRemove={removeItem}
          t={t}
        />
      )}
    </>
  );
};

function AnimatedQty({ value, testId }) {
  const prevRef = useRef(value);
  const [dir, setDir] = useState(null);

  useEffect(() => {
    if (value === prevRef.current) return undefined;
    setDir(value > prevRef.current ? 'up' : 'down');
    prevRef.current = value;
    const timer = setTimeout(() => setDir(null), 260);
    return () => clearTimeout(timer);
  }, [value]);

  const dirClass = dir === 'up' ? styles.qtyValueUp : dir === 'down' ? styles.qtyValueDown : '';

  return (
    <span
      className={styles.qtyValueWrap}
      aria-live="polite"
      aria-atomic="true"
      data-testid={testId}
    >
      <span key={value} className={`${styles.qtyValue} ${dirClass}`}>
        {value}
      </span>
    </span>
  );
}

function AnimatedMoney({ amount, className, currency, suffix = '' }) {
  const prevRef = useRef(amount);
  const [bump, setBump] = useState(false);

  useEffect(() => {
    if (Math.abs(amount - prevRef.current) < 0.001) return undefined;
    setBump(true);
    prevRef.current = amount;
    const timer = setTimeout(() => setBump(false), 360);
    return () => clearTimeout(timer);
  }, [amount]);

  // Whole when whole, the currency's decimals when not — never rounded away.
  const formatted = `${shopAmount(amount, currency)} ${suffix}`;

  return (
    <span
      key={formatted}
      className={`${className} ${bump ? styles.moneyBump : ''}`}
      aria-live="polite"
      aria-atomic="true"
    >
      {formatted}
    </span>
  );
}

function QtyButton({ className, onClick, children, ...props }) {
  const [pressed, setPressed] = useState(false);

  const handleClick = (e) => {
    setPressed(true);
    window.setTimeout(() => setPressed(false), 260);
    onClick?.(e);
  };

  return (
    <button
      type="button"
      {...props}
      className={`${styles.qtyBtn} ${pressed ? styles.qtyBtnPressed : ''} ${className || ''}`}
      onClick={handleClick}
    >
      {children}
    </button>
  );
}

function CartModal({ slug, items, total, pricing, products, addedVariantId = null, onClose, onClosing, onCheckout, onUpdateQty, onRemove, t, closeDurationMs = CLOSE_MS }) {
  const currency = useCurrency();
  const { i18n } = useTranslation();
  const isRtl = i18n?.dir?.() === 'rtl';
  // The live rows the drawer re-read, so a line can be struck the way its card is.
  const shelfById = React.useMemo(
    () => new Map((products ?? []).filter(Boolean).map((p) => [p.id, p])),
    [products],
  );
  const [confirmRemove, setConfirmRemove] = React.useState(null);
  const [recentlyRemoved, setRecentlyRemoved] = React.useState([]);
  const [closingOverlay, setClosingOverlay] = React.useState(false);
  const [closingSheet, setClosingSheet] = React.useState(false);
  // Whether the scroll area is moving right now — the one piece of state the
  // shared `quietScroll` bar keys its thumb off (see SCROLL_QUIET_MS above).
  const [scrolling, setScrolling] = React.useState(false);
  const closeTimerRef = useRef(null);

  const finishClose = React.useCallback(() => {
    clearTimeout(closeTimerRef.current);
    setClosingOverlay(false);
    setClosingSheet(false);
    onClose();
  }, [onClose]);

  const openedAtRef = useRef(Date.now());

  const requestClose = React.useCallback((afterClose, { animateSheet = true, how } = {}) => {
    if (closingOverlay) return;
    /* HOW THE SHOPPER LEFT THE DRAWER, once per close (a second request during the animation returned
     * above), so the ad pilot can tell "closed the drawer" from "did nothing". Never throws, never waits. */
    try {
      const shop = currentShop();
      trackStoreEvent?.({
        salonId: shop?.id ?? shop?._id,
        eventType: 'cart_drawer_close',
        payload: { how, seconds: Math.round((Date.now() - openedAtRef.current) / 1000) },
      });
    } catch { /* analytics never blocks the close */ }
    clearTimeout(closeTimerRef.current);
    onClosing?.();
    setClosingOverlay(true);
    setClosingSheet(animateSheet);
    closeTimerRef.current = setTimeout(() => {
      finishClose();
      afterClose?.();
    }, closeDurationMs);
  }, [closingOverlay, closeDurationMs, finishClose, onClosing]);

  React.useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  // Escape closes the TOPMOST layer: the remove confirm (a portal above the drawer) first, then the drawer
  // with the same animated close. A ref, so the listener always sees the current confirm.
  const confirmRemoveRef = useRef(null);
  confirmRemoveRef.current = confirmRemove;
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (confirmRemoveRef.current) { setConfirmRemove(null); return; }
      requestClose(undefined, { how: 'escape' });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [requestClose]);

  // The sheet opens sized to its content (so the "You may also like" row shows)
  // and the handle drags smoothly — both handled inside useDraggableSheet.
  const sheet = useDraggableSheet({
    onDismiss: () => requestClose(undefined, { animateSheet: false, how: 'swipe' }),
  });

  const requestRemove = (variantId) => setConfirmRemove(variantId);
  const cancelRemove = () => setConfirmRemove(null);
  const doRemove = () => {
    if (confirmRemove) {
      const removed = items.find((i) => i.variantId === confirmRemove);
      if (removed) {
        setRecentlyRemoved((prev) => pushRecentlyRemoved(prev, removed));
      }
      onRemove(confirmRemove);
    }
    setConfirmRemove(null);
  };

  /* A ref for the timer and state for the flag: the flag drives a paint, the
   * timer must not. Same shape as the side panel's, deliberately. */
  const scrollQuietTimer = useRef(0);
  useEffect(() => () => clearTimeout(scrollQuietTimer.current), []);
  const onScrollActivity = () => {
    setScrolling(true);
    clearTimeout(scrollQuietTimer.current);
    scrollQuietTimer.current = setTimeout(() => setScrolling(false), SCROLL_QUIET_MS);
  };

  /** `shownPrice`: one unit at the price its card showed, for the pixel (Context/CartContext). */
  const reAddRemoved = (entry, shownPrice) => {
    window.dispatchEvent(new CustomEvent('stylon:add-to-cart', {
      detail: {
        salonSlug: slug,
        productId: entry.productId,
        variantId: entry.variantId,
        quantity: entry.quantity ?? 1,
        snapshot: entry.snapshot,
        shownPrice,
      },
    }));
    window.dispatchEvent(new CustomEvent('stylon:cart-pulse'));
  };

  return (
    <div
      className={`${styles.modalOverlay} ${closingOverlay ? styles.modalOverlayClosing : ''}`}
      role="dialog"
      aria-modal="true"
      data-testid="cart-modal"
      onClick={() => requestClose(undefined, { how: 'backdrop' })}
    >
      <div
        className={`${styles.modalSheet} ${closingSheet ? styles.modalSheetClosing : ''}`}
        style={closingSheet ? undefined : sheet.sheetStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.dragZone} {...sheet.bind}>
          <div
            className={styles.dragHandle}
            aria-hidden
            data-testid="cart-modal-drag-handle"
          />
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>
              {t('cart.title', { defaultValue: 'Your order' })}
            </h2>
            <button
              type="button"
              className={styles.modalClose}
              onClick={() => requestClose(undefined, { how: 'header' })}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              aria-label={t('cart.continueShopping', { defaultValue: 'Continue shopping' })}
              data-testid="cart-modal-close"
            >
              {/* A way BACK to the page, not a close (the owner, 2026-10-26: "continue, not closing — that
                * might feel like losing"; DK Store: 3 of the 4 shoppers who tapped the X left the site).
                * The chevron points to the start side, so it flips in RTL. */}
              {isRtl
                ? <FiChevronRight aria-hidden data-testid="cart-modal-close-chevron" data-points="right" />
                : <FiChevronLeft aria-hidden data-testid="cart-modal-close-chevron" data-points="left" />}
              <span>{t('cart.continueShopping', { defaultValue: 'Continue shopping' })}</span>
            </button>
          </div>
        </div>

        <div
          ref={sheet.contentRef}
          className={styles.modalScroll}
          onScroll={onScrollActivity}
          data-scrolling={scrolling ? 'true' : 'false'}
          data-testid="cart-modal-scroll"
        >
          {/* THE ADD, CONFIRMED — only when an add opened the drawer and the line
            * is really in it (a shelf that refused the add has nothing to confirm). */}
          {addedVariantId && items.some((i) => i.variantId === addedVariantId) && (
            <p className={styles.addedNote} role="status" data-testid="cart-modal-added">
              <FiCheckCircle className={styles.addedNoteIcon} aria-hidden />
              {t('cart.addedToCart', { defaultValue: 'Added to your cart' })}
            </p>
          )}
          <div className={styles.itemList} data-testid="cart-modal-items">
            {items.map((it) => (
              <SwipeableCartItem
                key={it.variantId}
                item={it}
                linePricing={pricing?.lines?.get(it.variantId)}
                shelfProduct={shelfById.get(it.productId)}
                campaigns={pricing?.campaigns}
                confirmRemove={confirmRemove}
                onUpdateQty={onUpdateQty}
                onRequestRemove={requestRemove}
                t={t}
              />
            ))}
          </div>

          {/* AN EMPTY BASKET HAS NO SUBTOTAL AND NOWHERE TO CHECK OUT TO.
            * It used to print "סכום ביניים 0 ₪" above a live "מעבר לתשלום"
            * that led to "הסל ריק" (UX-SHOP-22). It says it is empty and
            * offers the way back to the shop; "recently removed" below stays,
            * because it is exactly what an emptied basket wants to offer. */}
          {items.length === 0 && (
            <div className={styles.emptyState} data-testid="cart-modal-empty">
              <span
                className={styles.emptyIcon}
                style={{ maskImage: `url(${basketIcon})`, WebkitMaskImage: `url(${basketIcon})` }}
                aria-hidden
              />
              <p className={styles.emptyTitle}>{t('cart.emptyTitle', { defaultValue: 'Your cart is empty' })}</p>
              <p className={styles.emptyHint}>{t('cart.emptyHint', { defaultValue: 'Add products from the shop and they will be waiting here.' })}</p>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => requestClose(undefined, { how: 'continue' })}
                data-testid="cart-modal-continue-shopping"
              >
                {t('cart.continueShopping', { defaultValue: 'Continue shopping' })}
              </button>
            </div>
          )}

          {/* THE SAME THREE NUMBERS THE CHECKOUT PRINTS. With no discount the
            * drawer reads as it always did — one row — so a shop running no
            * campaign sees no change at all. */}
          {items.length === 0 ? null : pricing?.discountTotal > 0.005 ? (
            <>
              <div className={styles.summary} data-testid="cart-modal-subtotal">
                <span>{t('cart.subtotal', { defaultValue: 'Subtotal' })}</span>
                <span className={styles.summaryValue}>{shopPrice(pricing.subtotal, currency)}</span>
              </div>
              <div className={styles.summary} data-testid="cart-modal-discount">
                <span>
                  {t('cart.discount', { defaultValue: 'Discount' })}
                  {/* One name, then "+N" (UX-FINAL-20) — the storefront fixer's helper. */}
                  {pricing.campaignNames?.length ? ` · ${campaignNamesLabel(pricing.campaignNames, t)}` : ''}
                </span>
                {/* Isolated LTR, so the minus stays on the number's left in a Hebrew or Arabic row. */}
                <span className={styles.summaryValue} dir="ltr">−{shopPrice(pricing.discountTotal, currency)}</span>
              </div>
              <div className={styles.summary} data-testid="cart-modal-total">
                <span>{t('cart.total', { defaultValue: 'Total' })}</span>
                <AnimatedMoney amount={total} currency={currency} className={styles.summaryValue} suffix={currencySymbol(currency)} />
              </div>
            </>
          ) : (
            <div className={styles.summary} data-testid="cart-modal-total">
              <span>{t('cart.subtotal', { defaultValue: 'Subtotal' })}</span>
              <AnimatedMoney amount={total} currency={currency} className={styles.summaryValue} suffix={currencySymbol(currency)} />
            </div>
          )}

          {/* WHAT THE SERVER SAID THE SHOP NO LONGER SELLS is not in the total above
            * and will not go to checkout; said here, before the tap. */}
          {items.length > 0 && (pricing?.unavailable?.length ?? 0) > 0 && (
            <p className={styles.itemStockNote} role="status" data-testid="cart-modal-unavailable-note">
              {/* Only ask lines: say what is true of them. Any other line the shop stopped selling keeps the old words
                * (a mix of both reads as the old words — the line itself says which is which). */}
              {(pricing.asking?.length ?? 0) === pricing.unavailable.length
                ? t('cart.asksNotCharged', {
                  defaultValue: 'Items you order by asking the shop are not in the total and will be taken out of your cart when you continue.',
                })
                : t('cart.unavailableNotCharged', {
                  defaultValue: 'Items no longer available are not in the total and will be taken out of your cart when you continue.',
                })}
            </p>
          )}

          {items.length > 0 && (
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => requestClose(onCheckout, { how: 'checkout' })}
                data-testid="cart-modal-checkout"
              >
                {t('cart.checkout', { defaultValue: 'Continue to checkout' })}
              </button>
            </div>
          )}

          <CartSuggestions
            slug={slug}
            cartItems={items}
            campaigns={pricing?.campaigns}
            priceOf={pricing?.priceOf}
            recentlyRemoved={recentlyRemoved}
            onAddToCart={(p, variant, shownPrice) => {
              window.dispatchEvent(new CustomEvent('stylon:add-to-cart', {
                detail: {
                  salonSlug: slug, productId: p.id, variantId: variant.id, quantity: 1,
                  // The price the card showed for that option, for the pixel (Components/RecMiniCard).
                  shownPrice,
                  snapshot: {
                    name: p.name,
                    image: variant.images?.[0]?.url ?? null,
                    price: effectiveVariantPrice(variant) ?? 0,
                    // WHAT THE SHOP SAID IT HAS, carried with the line so the cart can
                    // refuse the 41st click without a round trip. `null` when the shop
                    // states no ceiling — see `utils/stockCap.js`.
                    maxQty: variantPurchaseCap(p, variant),
                    variantAttrs: {
                      color: variant.color ?? null,
                      size:  variant.size  ?? null,
                      style: variant.style ?? null,
                    },
                    options: variantOptionsIn(p, variant, i18n?.language, t),
                  },
                },
              }));
            }}
            onReAdd={reAddRemoved}
            onNavigate={() => requestClose(undefined, { how: 'suggestion' })}
            t={t}
          />

          {/* The sheet covers the page's own notice while it is open. A new tab, so the basket
            * and the open sheet stay where they are. */}
          <PrivacyNotice t={t} newTab className={styles.privacyNotice} />
        </div>
      </div>

      <CartRemoveConfirm
        open={!!confirmRemove}
        onCancel={cancelRemove}
        onConfirm={doRemove}
        t={t}
      />
    </div>
  );
}

function CartRemoveConfirm({ open, onCancel, onConfirm, t }) {
  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div
      className={styles.confirmOverlay}
      role="dialog"
      aria-modal="true"
      data-testid="cart-remove-confirm"
      onClick={onCancel}
    >
      <div
        className={styles.confirmSheet}
        onClick={(e) => e.stopPropagation()}
        dir="auto"
      >
        <div
          className={styles.confirmTitle}
          role="heading"
          aria-level={3}
          data-testid="cart-remove-confirm-title"
        >
          {t('cart.confirmRemoveTitle', { defaultValue: 'Are you sure?' })}
        </div>
        <p className={styles.confirmBody}>
          {t('cart.confirmRemoveBody', { defaultValue: 'You can add it back any time from the shop.' })}
        </p>
        <div className={styles.confirmActions}>
          <button type="button" className={styles.secondaryBtn} onClick={onCancel} data-testid="cart-remove-cancel">
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button type="button" className={styles.dangerBtn} onClick={onConfirm} data-testid="cart-remove-confirm-btn">
            <FiTrash2 aria-hidden />
            {t('cart.remove', { defaultValue: 'Remove' })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Split a cart line into a base product name + a small "features" line
// (e.g. "Red / XL"). Prefers the structured variantAttrs; falls back to the
// "Name — features" suffix some add-to-cart paths bake into the name.
// The options' `shown` words (utils/shopperText) lead when the line carries them.
function splitCartItemName(snapshot, fallback) {
  const snap = snapshot || {};
  const shownOptions = Array.isArray(snap.options) ? snap.options.filter((o) => o && o.value) : [];
  const attrs = shownOptions.length
    ? shownOptions.map((o) => o.shown || o.value)
    : snap.variantAttrs
      ? [snap.variantAttrs.color, snap.variantAttrs.size, snap.variantAttrs.style].filter(Boolean)
      : [];
  let baseName = snap.name ?? fallback;
  let features = snap.variantName || (attrs.length ? attrs.join(' / ') : '');
  if (features && typeof baseName === 'string' && baseName.endsWith(` — ${features}`)) {
    baseName = baseName.slice(0, -(features.length + 3));
  } else if (!features && typeof baseName === 'string' && baseName.includes(' — ')) {
    const idx = baseName.lastIndexOf(' — ');
    features = baseName.slice(idx + 3);
    baseName = baseName.slice(0, idx);
  }
  // A value with a number in it keeps its own direction in an Arabic or Hebrew line: «500 g», never «g 500» (S10).
  if (!snap.variantName && attrs.length && features === attrs.join(' / ')) {
    features = attrs.map((a) => (/\d/.test(String(a)) ? isolate(String(a)) : a)).join(' / ');
  }
  return { baseName, features };
}

function SwipeableCartItem({ item: it, linePricing, shelfProduct, campaigns, confirmRemove, onUpdateQty, onRequestRemove, t }) {
  const currency = useCurrency();
  const { i18n } = useTranslation();
  /* THE "WAS" PRICE IS THE CARD'S (UX-SHOP-17). The card strikes a product's
   * own compare-at when it has one (Ray-Ban: 600) and its list price only when a
   * campaign alone took something off; the drawer struck the list price (450)
   * either way. Read through the same rule, off the live row the drawer
   * re-read. Until that row arrives, the line pricing's own list price stands. */
  const liveVariant = (shelfProduct?.variants ?? []).find((v) => v?.id === it.variantId) || null;
  const shown = liveVariant && linePricing
    ? shownVariantPrice({ ...liveVariant, price: linePricing.listUnit }, shelfProduct, campaigns ?? [])
    : null;
  /* A LINE THAT DOES NOT DIVIDE INTO WHOLE AGOROT is shown as its own total
   * (UX-FINAL-2): three straps under "₪40 off a watches order" come to ₪50,
   * which is not 16.67 × 3. Its was-price is then the line's list total. A
   * line discounted only by an order amount (the card shows no discount) still
   * strikes its list price. */
  const lineOnly = !!linePricing && linePricing.unitPrice === null;
  const wasPrice = lineOnly
    ? (linePricing.discounted ? linePricing.lineSubtotal : null)
    : shown
      ? (shown.discounted ? shown.compareAt : (linePricing?.discounted ? linePricing.listUnit : null))
      : (linePricing?.discounted ? linePricing.listUnit : null);
  /* WHAT THE SHOP SAID IT HAS — read off the line, so the 41st click is
   * refused in the same frame it happens rather than after a round trip. A
   * product the shop does not count has no ceiling (`UNCOUNTED`), its `+`
   * stays alive, and no number is shown, because there is no number: see
   * `utils/stockCap.js` for what those eleven dk-store products actually
   * said in the source. */
  const cap = lineCap(it);
  const atCap = hasCap(cap) && it.quantity >= cap;
  /* THE SERVER SAID THE SHOP NO LONGER SELLS THIS LINE (the cart quote's
   * ITEM_UNAVAILABLE). It shows no price — it is not in the total — and no more
   * of it can be added; taking it out stays open. */
  const unavailable = linePricing?.unavailable === true;
  const asking = unavailable && linePricing?.asking === true;
  const swipe = useSwipeToReveal({ onTrigger: () => onRequestRemove(it.variantId) });
  const imageUrl = it.snapshot?.image ?? it.snapshot?.imageUrl ?? null;
  /* THE LINE IN THE READER'S LANGUAGE (utils/shopperText): the live row the drawer re-read names the product and its
   * options in the page's language now — a line added in Arabic reads in Hebrew after the switch; before that row
   * arrives, the words the line was added with. */
  const readable = shelfProduct ? localizedProduct(shelfProduct, i18n?.language) : null;
  const { baseName, features } = splitCartItemName(
    readable && liveVariant
      ? { ...it.snapshot, name: readable.name, options: variantOptionsIn(readable, liveVariant, i18n?.language, t) }
      : it.snapshot,
    t('cart.item', { defaultValue: 'Item' }),
  );
  const swiped = Math.abs(swipe.offset) > 2;
  const [qtyPulse, setQtyPulse] = useState(false);
  const prevQtyRef = useRef(it.quantity);

  useEffect(() => {
    if (it.quantity === prevQtyRef.current) return undefined;
    setQtyPulse(true);
    prevQtyRef.current = it.quantity;
    const timer = setTimeout(() => setQtyPulse(false), 260);
    return () => clearTimeout(timer);
  }, [it.quantity]);

  useEffect(() => {
    if (!confirmRemove) swipe.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmRemove]);

  return (
    <div className={`${styles.itemSwipeWrap} ${swiped ? styles.itemSwipeActive : ''}`}>
      <div className={styles.itemSwipeBg} aria-hidden={!swiped}>
        <button
          type="button"
          className={styles.itemSwipeDelete}
          onClick={() => onRequestRemove(it.variantId)}
          aria-label={t('cart.remove', { defaultValue: 'Remove' })}
          data-testid={`cart-item-${it.variantId}-swipe-delete`}
        >
          <FiTrash2 className={styles.itemSwipeDeleteIcon} aria-hidden />
          <span className={styles.itemSwipeDeleteLabel}>
            {t('cart.remove', { defaultValue: 'Remove' })}
          </span>
        </button>
      </div>
      <div
        ref={swipe.ref}
        className={styles.item}
        style={{ transform: `translateX(${swipe.offset}px)` }}
        data-testid={`cart-item-${it.variantId}`}
      >
        {imageUrl ? (
          <img src={renditionUrl(imageUrl, 480)} alt="" className={styles.itemImg} />
        ) : (
          <div className={styles.itemImgPlaceholder}>📦</div>
        )}
        <div className={styles.itemMain}>
          {/* THE BRAND THE CARD LED WITH (UX-SHOP-24). Half of what a shopper chose on a shop of 533 watches and
              jewellery pieces is the maker, and the basket dropped it. Carried on the line's own snapshot, so a
              basket filled before this existed simply has none. */}
          {it.snapshot?.brand && (
            <span className={styles.itemBrand} data-testid={`cart-item-${it.variantId}-brand`}>
              {it.snapshot.brand}
            </span>
          )}
          <span className={styles.itemName}>{baseName}</span>
          {features && (
            <span className={styles.itemVariant}>{features}</span>
          )}
          {/* The unit price the product card shows — campaigns counted by the
            * one display rule — struck against the list price when a campaign
            * takes something off. The basket's own total is the server's. */}
          {asking ? (
            /* A line the shop sells by asking (a saved basket, buy-again or a wishlist can still hold one): the shop
             * still has it, it is just not ordered from the cart. */
            <span
              className={styles.itemStockNote}
              data-testid={`cart-item-${it.variantId}-asks`}
              role="status"
            >
              {t('cart.itemAsksTheShop', { defaultValue: 'Ask the shop to order this' })}
            </span>
          ) : unavailable ? (
            <span
              className={styles.itemStockNote}
              data-testid={`cart-item-${it.variantId}-unavailable`}
              role="status"
            >
              {t('cart.itemUnavailable', { defaultValue: 'No longer available' })}
            </span>
          ) : (
          <span className={styles.itemPrice}>
            {lineOnly ? (
              <>
                {shopPrice(Number(linePricing.lineTotal), currency)}
                {' · '}
                {t('checkout.piecesCount', { defaultValue: '{{n}} pieces', count: it.quantity, n: it.quantity })}
              </>
            ) : shopPrice(Number(linePricing?.unitPrice ?? it.snapshot?.price ?? 0), currency)}
            {wasPrice != null && (
              <>
                {' '}
                <s className={styles.itemPriceWas} data-testid={`cart-item-${it.variantId}-was`}>
                  {shopPrice(Number(wasPrice), currency)}
                </s>
              </>
            )}
          </span>
          )}
          {atCap && !unavailable && (
            <span
              className={styles.itemStockNote}
              data-testid={`cart-item-${it.variantId}-cap-note`}
              role="status"
            >
              {t('cart.atStockLimit', { defaultValue: 'That is all the shop has ({{n}})', n: cap })}
            </span>
          )}
        </div>
        <div className={`${styles.itemQty} ${qtyPulse ? styles.itemQtyPulse : ''}`}>
          <QtyButton
            onClick={() => {
              if (it.quantity <= 1) onRequestRemove(it.variantId);
              else onUpdateQty(it.variantId, it.quantity - 1);
            }}
            aria-label={t('cart.decrement', { defaultValue: 'Decrease' })}
            data-testid={`cart-item-${it.variantId}-dec`}
          >
            −
          </QtyButton>
          <AnimatedQty value={it.quantity} testId={`cart-item-${it.variantId}-qty`} />
          <QtyButton
            onClick={() => { if (!atCap && !unavailable) onUpdateQty(it.variantId, it.quantity + 1); }}
            disabled={atCap || unavailable}
            aria-label={t('cart.increment', { defaultValue: 'Increase' })}
            data-testid={`cart-item-${it.variantId}-inc`}
          >
            +
          </QtyButton>
        </div>
      </div>
    </div>
  );
}

function filterCartRecommendations(list, variantIds, productIds) {
  const variantSet = new Set(variantIds);
  const productSet = new Set(productIds);
  return (list ?? []).filter((p) => {
    if (productSet.has(p.id)) return false;
    // Nothing on the shelf is not a suggestion (UX-SHOP-21). An uncounted
    // product has no ceiling and stays.
    const shelf = productPurchaseCap(p);
    if (hasCap(shelf) && shelf <= 0) return false;
    const variants = p.variants ?? [];
    return !variants.some((v) => variantSet.has(v.id));
  });
}

/** A suggestion's lowest price on its own rows, or null when it has none (an ask product, a payload with none). */
function lowestPriceOf(p) {
  if (isAskProduct(p) && hidesItsPrice(p)) return null;
  const prices = (p?.variants ?? []).map((v) => effectiveVariantPrice(v)).filter((n) => Number.isFinite(n) && n > 0);
  if (prices.length) return Math.min(...prices);
  const from = Number(p?.priceFrom);
  return Number.isFinite(from) && from > 0 ? from : null;
}

/**
 * SMALL ADD-ONS FIRST (Swe Sosetta run 3, panel S10). Beside a ₪150 tray the drawer suggested another ₪150 tray. A
 * suggestion that costs at most 40% of what the basket already holds is an add-on: those lead, the cheapest first; the
 * rest keep the order the server chose; a product the shop sells by asking (nothing to add) comes last.
 */
function smallAddOnsFirst(list, cartItems) {
  const basket = (cartItems ?? []).reduce((sum, i) => sum + (Number(i?.snapshot?.price) || 0) * Math.max(1, Number(i?.quantity) || 1), 0);
  const ceiling = basket * 0.4;
  const rows = (list ?? []).map((p, at) => ({ p, at, price: lowestPriceOf(p), asks: isAskProduct(p) }));
  const small = rows.filter((r) => !r.asks && r.price != null && basket > 0 && r.price <= ceiling)
    .sort((a, b) => a.price - b.price || a.at - b.at);
  const smallSet = new Set(small.map((r) => r.p));
  const rest = rows.filter((r) => !smallSet.has(r.p) && !r.asks);
  const asked = rows.filter((r) => r.asks);
  return [...small, ...rest, ...asked].map((r) => r.p);
}

function pushRecentlyRemoved(list, item, max = MAX_RECENTLY_REMOVED) {
  if (!item?.variantId) return list ?? [];
  const without = (list ?? []).filter((i) => i.variantId !== item.variantId);
  return [{ ...item, removedAt: Date.now() }, ...without].slice(0, max);
}

function visibleRecentlyRemoved(recentlyRemoved, cartItems) {
  const inCart = new Set((cartItems ?? []).map((i) => i.variantId));
  return (recentlyRemoved ?? []).filter((i) => i.variantId && !inCart.has(i.variantId));
}

/**
 * Wraps a horizontally-scrolling strip and overlays floating chevron buttons
 * on the edges — but only when the strip actually overflows in that direction,
 * so a short list shows no chevrons. RTL-aware: `scrollLeft` is normalised to a
 * signed "distance from the start edge" so the same math works whether the
 * browser reports RTL scroll positions as negative or positive.
 */
function ScrollableStrip({ className, children, testId }) {
  const { i18n } = useTranslation();
  const isRtl = i18n?.dir?.() === 'rtl';
  const stripRef = useRef(null);
  const [overflow, setOverflow] = useState({ start: false, end: false });

  const measure = React.useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 1) {
      setOverflow({ start: false, end: false });
      return;
    }
    // Distance scrolled away from the logical start edge, always >= 0.
    const startDist = Math.abs(el.scrollLeft);
    const atStart = startDist <= 1;
    const atEnd = startDist >= maxScroll - 1;
    setOverflow({ start: !atStart, end: !atEnd });
  }, []);

  useEffect(() => {
    measure();
    const el = stripRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', measure, { passive: true });
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener('resize', measure);
    return () => {
      el.removeEventListener('scroll', measure);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, children]);

  const scrollByDir = (dir) => {
    const el = stripRef.current;
    if (!el) return;
    // dir: 'start' | 'end' in logical terms; convert to a physical delta.
    const amount = Math.max(160, Math.round(el.clientWidth * 0.8));
    const logical = dir === 'end' ? amount : -amount;
    // In RTL the physical scroll direction is mirrored.
    el.scrollBy({ left: isRtl ? -logical : logical, behavior: 'smooth' });
  };

  return (
    <div className={styles.stripScroll}>
      {overflow.start && (
        <button
          type="button"
          className={`${styles.stripChevron} ${styles.stripChevronStart}`}
          onClick={() => scrollByDir('start')}
          aria-hidden
          tabIndex={-1}
          data-testid={testId ? `${testId}-chevron-start` : undefined}
        >
          {isRtl ? <FiChevronRight /> : <FiChevronLeft />}
        </button>
      )}
      <div ref={stripRef} className={className} data-testid={testId}>
        {children}
      </div>
      {overflow.end && (
        <button
          type="button"
          className={`${styles.stripChevron} ${styles.stripChevronEnd}`}
          onClick={() => scrollByDir('end')}
          aria-hidden
          tabIndex={-1}
          data-testid={testId ? `${testId}-chevron-end` : undefined}
        >
          {isRtl ? <FiChevronLeft /> : <FiChevronRight />}
        </button>
      )}
    </div>
  );
}

function CartSuggestions({
  slug, cartItems, campaigns, priceOf, recentlyRemoved, onAddToCart, onReAdd, onNavigate, t,
}) {
  const [recs, setRecs] = useState([]);
  const [recsLoaded, setRecsLoaded] = useState(false);
  const variantIds = cartItems.map((i) => i.variantId);
  const productIds = cartItems.map((i) => i.productId);
  const recentVisible = visibleRecentlyRemoved(recentlyRemoved, cartItems);
  // Base "you may also like" on the most recently added cart product (mirrors
  // the product page's related strip).
  const anchorProductId = productIds[productIds.length - 1];

  useEffect(() => {
    if (!slug || !anchorProductId) {
      setRecs([]);
      setRecsLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setRecsLoaded(false);
    getRelatedProducts(slug, anchorProductId, { limit: 12 }).then((list) => {
      if (!cancelled) {
        setRecs(smallAddOnsFirst(filterCartRecommendations(list, variantIds, productIds), cartItems));
        setRecsLoaded(true);
      }
    }).catch(() => {
      if (!cancelled) {
        setRecs([]);
        setRecsLoaded(true);
      }
    });
    return () => { cancelled = true; };
  }, [slug, anchorProductId, variantIds.join('|'), productIds.join('|')]);

  if (recs.length > 0) {
    return (
      <section className={styles.recsSection} data-testid="cart-modal-recommendations">
        <h3 className={styles.recsTitle}>
          {t('rec.youMayAlsoLike', { defaultValue: 'You may also like' })}
        </h3>
        <ScrollableStrip className={styles.recsStrip} testId="cart-recs-strip">
          {recs.map((p) => (
            <RecMiniCard key={p.id} product={p} salonSlug={slug} onAddToCart={onAddToCart} onNavigate={onNavigate} campaigns={campaigns} />
          ))}
        </ScrollableStrip>
      </section>
    );
  }

  if (!recsLoaded || recentVisible.length === 0) return null;

  return (
    <section className={styles.recsSection} data-testid="cart-modal-recently-removed">
      <h3 className={styles.recsTitle}>
        {t('cart.recentlyRemoved', { defaultValue: 'Recently removed' })}
      </h3>
      <p className={styles.recsHint}>
        {t('cart.recentlyRemovedHint', { defaultValue: 'Tap to add back to your order.' })}
      </p>
      <ScrollableStrip className={styles.recsStrip} testId="cart-recently-removed-strip">
        {recentVisible.map((entry) => (
          <RecentlyRemovedCard
            key={entry.variantId}
            entry={entry}
            unitPrice={priceOf ? priceOf({ ...entry, quantity: 1 }).unitPrice : undefined}
            onReAdd={(shownPrice) => onReAdd(entry, shownPrice)}
            t={t}
          />
        ))}
      </ScrollableStrip>
    </section>
  );
}

function RecentlyRemovedCard({ entry, unitPrice, onReAdd, t }) {
  const currency = useCurrency();
  const imageUrl = entry.snapshot?.image ?? entry.snapshot?.imageUrl ?? null;
  const name = entry.snapshot?.name ?? t('cart.item', { defaultValue: 'Item' });
  // Priced like a line in the cart it came out of: campaigns counted.
  const price = Number(unitPrice ?? entry.snapshot?.price ?? 0);

  return (
    <button
      type="button"
      className={styles.recentCard}
      // Put back at the price shown here, as far as the pixel is told.
      onClick={() => onReAdd(price)}
      data-testid={`cart-recent-${entry.variantId}`}
      aria-label={`${t('cart.addBack', { defaultValue: 'Add back' })} — ${name}`}
    >
      {imageUrl ? (
        <img src={renditionUrl(imageUrl, 480)} alt="" className={styles.recentCardImg} />
      ) : (
        <div className={styles.recentCardImgPlaceholder}>📦</div>
      )}
      <span className={styles.recentCardName}>{name}</span>
      <span className={styles.recentCardPrice}>{shopPrice(price, currency)}</span>
      <span className={styles.recentCardAdd} aria-hidden>+</span>
    </button>
  );
}

export default StickyCartBar;

export function CartAbandonmentTracker() {
  const location = useLocation();
  const params = useParams();
  const slugFromUrl = shopSlugOfPath(location.pathname);
  const slug = params.salonSlug || params.username || params.slug || slugFromUrl;
  const { items } = useCart(slug || '__none__');
  useCartAbandonmentHeartbeat(slug, items);
  return null;
}

export { filterCartRecommendations, pushRecentlyRemoved, visibleRecentlyRemoved, splitCartItemName, smallAddOnsFirst };
